import React from 'react';
import { Link, graphql } from 'gatsby';
import styled from 'styled-components';
import { variant } from 'styled-system';
import { useLang } from '../../hooks/useLang';
import Box from '../Box/Box';
import { inherits } from 'node:util';
import { decorate } from '@storybook/addon-actions';

const StyledLink = styled(Box)(
    {
        '& a': {
            display: 'inline-flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            transition: 'color 200ms ease',
        },
    },
    variant({
        variants: {
            primary: {
                ':hover': { color: 'primary.main' },
            },
            secondary: {
                color: 'primary.1000',
                borderBottom: '1px solid',
                borderColor: 'primary.1000',
                width: 'max-content',
                fontSize: '16px',
                paddingBottom: '4px',
            },
            white: {
                color: 'neutral.1000',
                borderBottom: '1px solid',
                borderColor: 'neutral.1000',
                width: 'max-content',
                fontSize: '16px',
                paddingBottom: '4px',
            },
            underline: {
                borderBottom: '1px solid',
                borderColor: 'currentColor',
                width: 'max-content',
            },
        },
    })
);

type PrismicLinkProps = {
    link: any;
    variant?: 'primary' | 'secondary' | 'white' | 'underline';
    target?: string;
    urlParams?: string;
    children: React.ReactNode;
    tabIndex?: number;
    state?: {
        [key: string]: any;
    };
};

const PrismicLink = ({
    link,
    variant,
    urlParams,
    target,
    children,
    state,
    tabIndex,
    ...props
}: PrismicLinkProps) => {
    const lang = useLang();
    return (
        <StyledLink variant={variant} {...props}>
            {(() => {
                switch (link?.document?.__typename) {
                    case 'PrismicPage':
                        return (
                            <Link
                                tabIndex={tabIndex}
                                to={`${
                                    lang.defaultLang ? '' : `/${lang.prefix}`
                                }/${link?.document?.data?.slug || ''}${
                                    urlParams || ''
                                }`}
                                state={state}
                            >
                                {children}
                            </Link>
                        );

                    case 'PrismicSection':
                        return (
                            <a
                                tabIndex={tabIndex}
                                href={`#${link?.document?.uid}${
                                    urlParams || ''
                                }`}
                            >
                                {children}
                            </a>
                        );
                    default:
                        return (
                            <a
                                tabIndex={tabIndex}
                                href={`${link.url}${urlParams || ''}`}
                                target={target || link.target || '_blank'}
                                rel="noopener noreferrer"
                            >
                                {children}
                            </a>
                        );
                }
            })()}
        </StyledLink>
    );
};

export const _ = graphql`
    fragment PRISMIC_LINK_FRAGMENT on PrismicLinkType {
        document {
            __typename
            ... on PrismicPage {
                data {
                    slug
                }
            }
            ... on PrismicSection {
                uid
            }
        }
        type
        url
        target
    }
`;

export default PrismicLink;
